import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Identity {
  id: string;
  type: 'user' | 'client';
  client: string;
  exp: number;
  roles: string[];
  permissions: string[];
  scope: string[];
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
}

interface AuthContextType {
  identity: Identity | null;
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
}

// Admin email list
const ADMIN_EMAILS = [
  'tyler.rose@angel.com',
  'jacob.bell@angel.com',
  'melody.mcardle@angel.com',
  'ray.willardson@angel.com',
];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finops-portal.angel-admin.angelstudios.com';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
    
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      setError(errorParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        withCredentials: true
      });
      setIdentity(response.data.identity);
      setUser(response.data.user || null);
      setError(null);
    } catch (err) {
      setIdentity(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    window.location.href = `${API_BASE_URL}/api/auth/login`;
  };

  const logout = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      setIdentity(null);
      setUser(null);
      if (response.data.logoutUrl) {
        window.location.href = response.data.logoutUrl;
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const hasRole = (role: string): boolean => {
    return identity?.roles?.includes(role) ?? false;
  };

  const hasPermission = (permission: string): boolean => {
    return identity?.permissions?.includes(permission) ?? false;
  };

  const value: AuthContextType = {
    identity,
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!identity,
    isAdmin: user ? ADMIN_EMAILS.includes(user.email.toLowerCase()) : false,
    hasRole,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
